import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import SecurityCategoryDetail from './SecurityCategoryDetail';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from '../config';

/**
 * Security Categories Component
 */
const SecurityCategories = ({ securityCategories, expandedCategory, setExpandedCategory }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRemediating, setIsRemediating] = useState(false);
  
  // Calculate summary statistics
  const totalControls = securityCategories.reduce(
    (total, category) => total + category.items.length, 
    0
  );
  
  const passedControls = securityCategories.reduce(
    (total, category) => total + category.items.filter(item => item.status === 'passed').length, 
    0
  );
  
  const warningControls = securityCategories.reduce(
    (total, category) => total + category.items.filter(item => item.status === 'warning').length, 
    0
  );
  
  const failedControls = securityCategories.reduce(
    (total, category) => total + category.items.filter(item => item.status === 'failed').length, 
    0
  );

  // Function to trigger security tests
  const refreshSecurityTests = async () => {
    setIsRefreshing(true);
    try {
      // Call the API endpoint to run security tests
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.INFRASTRUCTURE.RUN_TESTS}`,
        {},
        { 
          headers: REQUEST_CONFIG.getAuthHeaders(),
          timeout: REQUEST_CONFIG.timeout
        }
      );
      
      // Reload the page to show updated results
      window.location.reload();
    } catch (error) {
      console.error('Error running security tests:', error);
      setIsRefreshing(false);
    }
  };

  // Function to trigger remediation for all failing tests
  const remediateAllIssues = async () => {
    setIsRemediating(true);
    try {
      // First call the remediation endpoint
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.INFRASTRUCTURE.REMEDIATE}`,
        {},
        { 
          headers: REQUEST_CONFIG.getAuthHeaders(),
          timeout: REQUEST_CONFIG.timeout
        }
      );
      
      // Then call the run tests endpoint to update the test results
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.INFRASTRUCTURE.RUN_TESTS}`,
        {},
        { 
          headers: REQUEST_CONFIG.getAuthHeaders(),
          timeout: REQUEST_CONFIG.timeout
        }
      );
      
      // Reload the page to show updated results
      window.location.reload();
    } catch (error) {
      console.error('Error remediating issues:', error);
      setIsRemediating(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Security Controls</h2>
        <div className="flex space-x-3">
          <button 
            onClick={remediateAllIssues}
            disabled={isRemediating || failedControls === 0}
            className="flex items-center px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-400"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
            </svg>
            {isRemediating ? 'Remediating...' : 'Remediate All'}
          </button>
          <button 
            onClick={refreshSecurityTests}
            disabled={isRefreshing}
            className="flex items-center px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md transition-colors"
          >
            <RefreshCw size={16} className={`mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Running Tests...' : 'Run Tests'}
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="p-4 rounded-lg border bg-white border-gray-200">
          <h3 className="font-bold text-gray-800 mb-2">Overall Status</h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Total Controls: {totalControls}</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              failedControls > 0 
                ? 'bg-red-100 text-red-800' 
                : warningControls > 0 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : 'bg-green-100 text-green-800'
            }`}>
              {failedControls > 0 
                ? 'Action Required' 
                : warningControls > 0 
                  ? 'Needs Attention' 
                  : 'Compliant'}
            </span>
          </div>
        </div>
        
        <div className="p-4 rounded-lg bg-white border border-gray-200">
          <h3 className="font-bold text-green-600 mb-2 flex items-center">
            <CheckCircle size={16} className="mr-1" />
            Passed
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-700">{passedControls} Controls</span>
            <span className="text-gray-500 text-sm">
              {Math.round((passedControls / totalControls) * 100)}%
            </span>
          </div>
        </div>
        
        <div className="p-4 rounded-lg bg-white border border-gray-200">
          <h3 className="font-bold text-yellow-600 mb-2 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            Warning
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-700">{warningControls} Controls</span>
            <span className="text-gray-500 text-sm">
              {Math.round((warningControls / totalControls) * 100)}%
            </span>
          </div>
        </div>
        
        <div className="p-4 rounded-lg bg-white border border-gray-200">
          <h3 className="font-bold text-red-600 mb-2 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            Failed
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-700">{failedControls} Controls</span>
            <span className="text-gray-500 text-sm">
              {Math.round((failedControls / totalControls) * 100)}%
            </span>
          </div>
        </div>
      </div>
      
      {/* Category Details */}
      {securityCategories.map(category => (
        expandedCategory === category.id && (
          <SecurityCategoryDetail 
            key={category.id}
            category={category}
          />
        )
      ))}
      
      {/* Category List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {securityCategories.map(category => {
          const categoryPassedCount = category.items.filter(item => item.status === 'passed').length;
          const categoryWarningCount = category.items.filter(item => item.status === 'warning').length;
          const categoryFailedCount = category.items.filter(item => item.status === 'failed').length;
          const categoryTotal = category.items.length;
          const compliancePercentage = Math.round((categoryPassedCount / categoryTotal) * 100);
          
          // Determine compliance status color
          const complianceStatusColor = categoryFailedCount > 0 
            ? 'bg-red-100 text-red-800' 
            : categoryWarningCount > 0 
              ? 'bg-yellow-100 text-yellow-800' 
              : 'bg-green-100 text-green-800';
              
          return (
            <div 
              key={category.id} 
              className={`p-4 rounded-lg border ${expandedCategory === category.id ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'} cursor-pointer hover:bg-gray-50`}
              onClick={() => setExpandedCategory(category.id)}
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-gray-800 flex items-center">
                  {category.icon}
                  <span className="ml-2">{category.title}</span>
                </h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${complianceStatusColor}`}>
                  {compliancePercentage}% Compliant
                </span>
              </div>
              
              <div className="flex flex-wrap items-center mb-2 text-sm">
                <span className="inline-flex items-center mr-3 text-green-600">
                  <CheckCircle size={14} className="mr-1" />
                  {categoryPassedCount} Passed
                </span>
                <span className="inline-flex items-center mr-3 text-yellow-600">
                  <AlertCircle size={14} className="mr-1" />
                  {categoryWarningCount} Warning
                </span>
                <span className="inline-flex items-center text-red-600">
                  <AlertCircle size={14} className="mr-1" />
                  {categoryFailedCount} Failed
                </span>
              </div>
              
              <div className="text-xs text-gray-500 flex items-center">
                <Clock size={12} className="mr-1" />
                Last checked: 26/03/2025
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SecurityCategories;
